/**
 * mailserver.ts - Mail server configuration module
 * 
 * CHANGE HISTORY:
 * - 251030_1000 [pending] danielbeer: Created mail server config
 * 
 * Centralizes configuration for Mailr container (Postfix, Dovecot, email processing).
 * Tenant-level only - mail server settings are infrastructure-level.
 */

export const mailServerModule = {
  id: 'services-mailserver',
  name: 'Mail Server',
  icon: '📧',
  category: 'services',
  order: 2,
  description: 'Configure Mailr mail server (Postfix, Dovecot, SMTP)',
  
  settings: {
    // === GENERAL MAIL SERVER SETTINGS ===
    
    mailServerEnabled: {
      type: 'boolean',
      label: 'Enable Mail Server',
      description: 'Enable Mailr container mail server',
      defaultValue: true,
      scope: ['tenant']
    },
    
    domain: {
      type: 'string',
      label: 'Mail Domain',
      description: 'Primary domain for email (e.g., company.com)',
      defaultValue: '',
      placeholder: 'company.com',
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\\.[a-zA-Z]{2,}$',
        message: 'Must be a valid domain name'
      }
    },
    
    hostname: {
      type: 'string',
      label: 'Mail Server Hostname',
      description: 'FQDN for mail server (e.g., mail.company.com)',
      defaultValue: '',
      placeholder: 'mail.company.com',
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^[a-zA-Z0-9][a-zA-Z0-9-.]+[a-zA-Z0-9]$',
        message: 'Must be a valid hostname'
      }
    },
    
    // === SMTP SETTINGS (Postfix) ===
    
    smtpPort: {
      type: 'number',
      label: 'SMTP Port',
      description: 'Port for SMTP connections',
      defaultValue: 25,
      options: [
        { label: '25 (Standard)', value: 25 },
        { label: '587 (Submission)', value: 587 },
        { label: '465 (SMTPS)', value: 465 }
      ],
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    smtpTlsEnabled: {
      type: 'boolean',
      label: 'Enable SMTP TLS',
      description: 'Require TLS for SMTP connections',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    smtpAuthRequired: {
      type: 'boolean',
      label: 'Require SMTP Authentication',
      description: 'Require authentication for sending email',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    smtpRelayHost: {
      type: 'string',
      label: 'SMTP Relay Host',
      description: 'Optional relay host for outbound email',
      defaultValue: '',
      placeholder: 'smtp.sendgrid.net',
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    smtpRelayPort: {
      type: 'number',
      label: 'SMTP Relay Port',
      description: 'Port for relay host',
      defaultValue: 587,
      min: 1,
      max: 65535,
      visible: (settings) => settings['services-mailserver.smtpRelayHost'],
      scope: ['tenant']
    },
    
    smtpRelayUsername: {
      type: 'string',
      label: 'Relay Username',
      description: 'Username for relay authentication',
      defaultValue: '',
      placeholder: 'apikey',
      visible: (settings) => settings['services-mailserver.smtpRelayHost'],
      scope: ['tenant']
    },
    
    smtpRelayPassword: {
      type: 'password',
      label: 'Relay Password',
      description: 'Password for relay authentication',
      defaultValue: '',
      placeholder: 'Enter relay password',
      visible: (settings) => settings['services-mailserver.smtpRelayHost'],
      scope: ['tenant'],
      sensitive: true
    },
    
    // === IMAP SETTINGS (Dovecot) ===
    
    imapEnabled: {
      type: 'boolean',
      label: 'Enable IMAP',
      description: 'Enable IMAP access to mailboxes',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    imapPort: {
      type: 'number',
      label: 'IMAP Port',
      description: 'Port for IMAP connections',
      defaultValue: 143,
      options: [
        { label: '143 (Standard)', value: 143 },
        { label: '993 (IMAPS)', value: 993 }
      ],
      visible: (settings) => settings['services-mailserver.imapEnabled'],
      scope: ['tenant']
    },
    
    imapTlsEnabled: {
      type: 'boolean',
      label: 'Enable IMAP TLS',
      description: 'Require TLS for IMAP connections',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.imapEnabled'],
      scope: ['tenant']
    },
    
    // === SECURITY SETTINGS ===
    
    dkimEnabled: {
      type: 'boolean',
      label: 'Enable DKIM Signing',
      description: 'Sign outbound email with DKIM',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    dkimSelector: {
      type: 'string',
      label: 'DKIM Selector',
      description: 'DKIM selector for DNS records',
      defaultValue: 'default',
      placeholder: 'default',
      visible: (settings) => settings['services-mailserver.dkimEnabled'],
      scope: ['tenant']
    },
    
    spfEnabled: {
      type: 'boolean',
      label: 'Enable SPF Validation',
      description: 'Validate SPF records on inbound email',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    dmarcEnabled: {
      type: 'boolean',
      label: 'Enable DMARC',
      description: 'Enable DMARC policy enforcement',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    dmarcPolicy: {
      type: 'select',
      label: 'DMARC Policy',
      description: 'DMARC policy for failed authentication',
      defaultValue: 'quarantine',
      options: [
        { label: 'None (Monitor only)', value: 'none' },
        { label: 'Quarantine', value: 'quarantine' },
        { label: 'Reject', value: 'reject' }
      ],
      visible: (settings) => settings['services-mailserver.dmarcEnabled'],
      scope: ['tenant']
    },
    
    // === ANTI-SPAM SETTINGS ===
    
    spamFilterEnabled: {
      type: 'boolean',
      label: 'Enable Spam Filter',
      description: 'Enable SpamAssassin filtering',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    spamThreshold: {
      type: 'number',
      label: 'Spam Score Threshold',
      description: 'Score above which email is marked as spam',
      defaultValue: 5.0,
      min: 1.0,
      max: 10.0,
      step: 0.5,
      visible: (settings) => settings['services-mailserver.spamFilterEnabled'],
      scope: ['tenant']
    },
    
    spamAction: {
      type: 'select',
      label: 'Spam Action',
      description: 'What to do with spam',
      defaultValue: 'tag',
      options: [
        { label: 'Tag Only', value: 'tag' },
        { label: 'Move to Spam Folder', value: 'move' },
        { label: 'Reject', value: 'reject' }
      ],
      visible: (settings) => settings['services-mailserver.spamFilterEnabled'],
      scope: ['tenant']
    },
    
    // === EMAIL COMMAND PROCESSING (Parsr) ===
    
    commandProcessingEnabled: {
      type: 'boolean',
      label: 'Enable Email Commands',
      description: 'Process email commands via Parsr',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    commandConfirmationRequired: {
      type: 'boolean',
      label: 'Require Command Confirmation',
      description: 'Require confirmation for destructive commands',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.commandProcessingEnabled'],
      scope: ['tenant']
    },
    
    commandConfirmationTimeout: {
      type: 'number',
      label: 'Confirmation Timeout (seconds)',
      description: 'How long to wait for command confirmation',
      defaultValue: 300,
      min: 30,
      max: 3600,
      visible: (settings) => settings['services-mailserver.commandConfirmationRequired'],
      scope: ['tenant']
    },
    
    // === STORAGE SETTINGS ===
    
    mailboxQuota: {
      type: 'number',
      label: 'Default Mailbox Quota (MB)',
      description: 'Default storage quota per mailbox',
      defaultValue: 1024,
      min: 100,
      max: 10240,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    messageRetention: {
      type: 'number',
      label: 'Message Retention (days)',
      description: 'Auto-delete messages older than this',
      defaultValue: 365,
      min: 30,
      max: 3650,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    // === WEBMAIL SETTINGS ===
    
    webmailEnabled: {
      type: 'boolean',
      label: 'Enable Webmail',
      description: 'Enable webmail interface',
      defaultValue: true,
      visible: (settings) => settings['services-mailserver.mailServerEnabled'],
      scope: ['tenant']
    },
    
    webmailUrl: {
      type: 'string',
      label: 'Webmail URL',
      description: 'URL for webmail interface',
      defaultValue: '',
      placeholder: 'https://webmail.company.com',
      visible: (settings) => settings['services-mailserver.webmailEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^https?://.+',
        message: 'Must be a valid URL'
      }
    }
  }
};

export default mailServerModule;
